import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Lightning, NotePencil, MagnifyingGlass, Flask, CircleNotch, CheckSquare } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { GhostButton, PrimaryButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { VanPreview } from "@/components/admin/production/VanPreview";
import { RuleList } from "./RuleList";
import { RuleEditor } from "./RuleEditor";
import { AssistantPanel } from "./AssistantPanel";
import { RunAllDialog } from "./RunAllDialog";
import { ReviewDialog } from "./ReviewDialog";
import { SelectionBar } from "./SelectionBar";
import { makeHubName } from "./ruleText";

const FILTERS = [["all", "All"], ["draft", "Drafts"], ["off", "Turned off"]];

function useRulebook() {
  const [data, setData] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/rulebook", { timeout: 60000 }).then(({ data: d }) => setData(d)).catch((e) => toast.error(getErrorMessage(e, "Couldn't load the rulebook"))), []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

function useSelection() {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const onCheck = useCallback((r) => setSelected((s) => { const n = new Set(s); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; }), []);
  const addAll = (rules) => setSelected((s) => new Set([...s, ...rules.map((r) => r.id)]));
  const clear = () => setSelected(new Set());
  const stop = () => { setSelecting(false); clear(); };
  return { selecting, setSelecting, selected, onCheck, addAll, clear, stop };
}

function Toolbar({ q, setQ, filter, setFilter, counts, onNew, onTest, onReview, reviewCount, onRun, selecting, onSelect }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-6">
      <label className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-full px-3.5 py-2 w-full sm:w-64 focus-within:border-[#73bf44]">
        <MagnifyingGlass size={14} className="text-neutral-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rules…" data-testid="rules-search" className="bg-transparent outline-none text-sm text-white w-full placeholder:text-neutral-600" />
      </label>
      {FILTERS.map(([k, l]) => (
        <button key={k} onClick={() => setFilter(k)} data-testid={`rules-filter-${k}`} className={`text-xs px-3 py-2 rounded-full border transition-colors ${filter === k ? "border-[#73bf44] text-white bg-[#73bf44]/10" : "border-neutral-800 text-neutral-400 hover:text-white"}`}>
          {l}{counts[k] != null ? ` · ${counts[k]}` : ""}
        </button>
      ))}
      <div className="flex flex-wrap gap-2 ml-auto">
        <GhostButton onClick={onSelect} data-testid="rules-select-mode" className={selecting ? "!border-[#73bf44] !text-white" : ""}><CheckSquare size={15} /> {selecting ? "Done selecting" : "Select"}</GhostButton>
        <GhostButton onClick={onNew} data-testid="rules-new"><Plus size={15} /> Add rule</GhostButton>
        <GhostButton onClick={onTest} data-testid="rules-test-van"><Flask size={15} /> Test a van</GhostButton>
        <GhostButton onClick={onReview} data-testid="rules-review"><NotePencil size={15} /> Notes to review{reviewCount ? ` · ${reviewCount}` : ""}</GhostButton>
        <PrimaryButton onClick={onRun} data-testid="rules-run-all"><Lightning size={15} weight="fill" /> Run on all vans</PrimaryButton>
      </div>
    </div>
  );
}

export function RulebookTab({ prefill, onPrefillUsed, onChanged }) {
  const [data, load] = useRulebook();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [dialog, setDialog] = useState("");
  const [ask, setAsk] = useState(prefill || "");
  const [reviewCount, setReviewCount] = useState(null);
  useEffect(() => { if (prefill) { setAsk(prefill); onPrefillUsed(); } }, [prefill, onPrefillUsed]);
  useEffect(() => { adminApi.get("/admin/rulebook/review", { timeout: 60000 }).then(({ data: d }) => setReviewCount(d.length)).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0);
  const sel = useSelection();
  const refresh = useCallback(async () => { onChanged(); setBusy(true); await load(); setBusy(false); }, [load, onChanged]);
  const hubName = useMemo(() => makeHubName(data?.hubs), [data]);
  const clearAsk = useCallback(() => setAsk(""), []);

  const act = (fn, ok) => async (r, extra) => {
    if (busy) return;
    setBusy(true);
    try { await fn(r, extra); if (ok) toast.success(ok); await refresh(); } catch (e) { toast.error(getErrorMessage(e)); setBusy(false); }
  };
  const pick = (r) => ({ title: r.title, stage: r.stage, conditions: r.conditions, actions: r.actions, description: r.description || "", enabled: r.enabled });
  const handlers = {
    onEdit: (r) => setEditing(r),
    onApprove: act((r) => adminApi.post(`/admin/rulebook/${r.id}/approve`), "Rule approved — it now runs on every sync"),
    onDelete: async (r) => {
      if (!window.confirm(r.status === "draft" ? "Discard this draft?" : `Delete “${r.title}”?`)) return;
      await act(() => adminApi.delete(`/admin/rulebook/${r.id}`))(r);
    },
    onToggle: act((r) => adminApi.put(`/admin/rulebook/${r.id}`, { ...pick(r), enabled: !r.enabled })),
    onMove: act((r, dir) => adminApi.post(`/admin/rulebook/${r.id}/move`, { direction: dir })),
  };
  const bulkDelete = async (rules) => {
    setBusy(true);
    try {
      const { data: d } = await adminApi.post("/admin/rulebook/bulk-delete", { ids: rules.map((r) => r.id) });
      toast.success(`Deleted ${d.deleted} rule${d.deleted === 1 ? "" : "s"}`);
      sel.stop(); setRev((v) => v + 1);
      await refresh();
    } catch (e) { toast.error(getErrorMessage(e)); setBusy(false); }
  };

  if (!data) return <div className="text-neutral-600">Loading rulebook…</div>;
  const counts = { all: data.rules.length, draft: data.rules.filter((r) => r.status === "draft").length, off: data.rules.filter((r) => !r.enabled && r.status !== "draft").length };
  const needle = q.trim().toLowerCase();
  const shown = data.rules.filter((r) => (filter === "all" || (filter === "draft" ? r.status === "draft" : !r.enabled && r.status !== "draft"))
    && (!needle || JSON.stringify([r.title, r.conditions, r.actions, r.description]).toLowerCase().includes(needle)));

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-6 items-start" data-testid="rulebook-tab">
      <div className="min-w-0">
        <Toolbar q={q} setQ={setQ} filter={filter} setFilter={setFilter} counts={counts} reviewCount={reviewCount}
          onNew={() => setEditing({ stage: "specs", conditions: [], actions: [{ field: "battery_configuration", op: "set", value: "" }] })}
          onTest={() => setDialog("test")} onReview={() => setDialog("review")} onRun={() => setDialog("run")}
          selecting={sel.selecting} onSelect={() => (sel.selecting ? sel.stop() : sel.setSelecting(true))} />
        {sel.selecting && (
          <SelectionBar selectedRules={data.rules.filter((r) => sel.selected.has(r.id))} shownCount={shown.length}
            onSelectAll={() => sel.addAll(shown)} onClear={sel.clear} onDone={sel.stop} onDelete={bulkDelete} />
        )}
        <p className="text-xs text-neutral-500 mb-6 flex items-center gap-2">
          {data.vans} vans checked. Rules run top to bottom in each stage, and later rules win. Only approved rules run.
          {busy && <span className="inline-flex items-center gap-1 text-[#73bf44]" data-testid="rules-updating"><CircleNotch size={12} className="animate-spin" /> Updating…</span>}
        </p>
        <div className={`transition-opacity ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          <RuleList rules={shown} schema={data.schema} hubName={hubName} handlers={handlers} selection={sel} />
        </div>
      </div>
      <AssistantPanel rev={rev} prefill={ask} onPrefillUsed={clearAsk} onChanged={refresh} cardProps={{ schema: data.schema, hubName, vanCounts: Object.fromEntries(data.rules.map((r) => [r.id, r.van_count])), ...handlers }} />
      {editing && <RuleEditor rule={editing} schema={data.schema} hubs={data.hubs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      <RunAllDialog open={dialog === "run"} onClose={() => setDialog("")} />
      <ReviewDialog open={dialog === "review"} onClose={() => setDialog("")} onCount={setReviewCount}
        onAsk={(it) => setAsk(`Notes on ${it.chassis_number} (${it.model}): "${it.notes}"\nWrite a rule so vans with notes like this are set up correctly.`)} />
      <Dialog open={dialog === "test"} onOpenChange={(o) => !o && setDialog("")}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-5xl max-h-[90vh] overflow-y-auto p-0"><VanPreview /></DialogContent>
      </Dialog>
    </div>
  );
}
